import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Infinite-scroll + scroll-position preservation for a scrollable MUI Joy Select
 * listbox (or any scroll container rendered via `slotProps.listbox`).
 *
 * - `onScroll` auto-loads the next page when the user scrolls near the bottom and
 *   there are more rows.
 * - After rows append, the pre-load `scrollTop` is *held* across the next several
 *   animation frames. MUI Joy Select uses DOM focus management and re-syncs the
 *   listbox (its `itemsChange` effect) AFTER paint, which snaps the scroll back to
 *   the selected/highlighted (top) option. A single layout-effect restore runs
 *   too early and gets overwritten, so we reassert for a few frames — but only
 *   when the scroll has jumped BACKWARD, so we never fight the user scrolling on.
 *
 * Attach `listboxRef` and `onScroll` to the listbox slot:
 *   slotProps={{ listbox: { ref: listboxRef, onScroll, sx: { maxHeight, overflow: 'auto' } } }}
 *
 * @param {Object} opts
 * @param {boolean} opts.hasMore   More pages available.
 * @param {boolean} opts.busy      A fetch is in flight (first page or load-more).
 * @param {()=>void} opts.loadMore Append the next page.
 * @param {number}  opts.itemCount Current row count; drives the restore effect.
 * @param {number}  [opts.threshold=48] Px from the bottom that triggers auto-load.
 * @param {number}  [opts.holdFrames=8] Frames to reassert the position after append.
 * @returns {{listboxRef:(node:HTMLElement|null)=>void, onScroll:(e:any)=>void}}
 */
export default function useListboxPaging({
  hasMore,
  busy,
  loadMore,
  itemCount,
  threshold = 48,
  holdFrames = 8,
}) {
  const nodeRef = useRef(null);
  // Non-null while a scroll position is waiting to be restored after an append.
  const restoreTopRef = useRef(null);
  const rafRef = useRef(0);

  const listboxRef = useCallback((node) => {
    nodeRef.current = node;
  }, []);

  const requestMore = useCallback(() => {
    if (!hasMore || busy) return;
    if (nodeRef.current) restoreTopRef.current = nodeRef.current.scrollTop;
    loadMore();
  }, [hasMore, busy, loadMore]);

  const onScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      // Capture the scroll node here too, in case the slot ref wasn't forwarded.
      nodeRef.current = el;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) requestMore();
    },
    [requestMore, threshold],
  );

  useLayoutEffect(() => {
    const top = restoreTopRef.current;
    const el = nodeRef.current;
    if (top == null || !el) return undefined;
    restoreTopRef.current = null;

    // Reassert across the next few frames to outlast MUI's post-paint reposition,
    // correcting only backward jumps so forward user-scroll isn't blocked.
    let frames = 0;
    const hold = () => {
      if (el.scrollTop < top - 1) el.scrollTop = top;
      if (++frames < holdFrames) rafRef.current = requestAnimationFrame(hold);
    };
    el.scrollTop = top;
    rafRef.current = requestAnimationFrame(hold);
    return () => cancelAnimationFrame(rafRef.current);
  }, [itemCount, holdFrames]);

  return { listboxRef, onScroll };
}
